import { DiscussionFormKeys } from '@discussion/DiscussionList.models';
import { IProjectDiscussion } from '@discussion/projectDiscussion/projectDiscussion.models';
import { useConfirmationModal } from '@common/confirmation.hooks';
import {
  Button,
  ComponentType,
  FormCheckbox,
  FormFieldMethods,
  FormTextarea,
  Grid,
  LoadingButton,
  requiredRule,
} from '@gcl/react-core';
import { TagItemView } from '@masterSchedule/detail/tagView/TagItemView';
import * as React from 'react';
import './DraftDiscussionRow.scss';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { QueryKey } from '@tanstack/react-query';
import { useMyUserProfile } from '@config/config.hooks';
import { DiscussionRowLayout } from './DiscussionRowLayout';

interface IDraftDiscussionRowProps {
  discussion: IProjectDiscussion;
  onSelected: (id: number, isSelected: boolean) => void;
  formFieldMethods: FormFieldMethods;
  onDeleteDiscussion?: (id: number) => void;
  isLoadingDeleteDiscussion?: boolean;
  isErrorDeleteDiscussion?: boolean;
  isSuccessDeleteDiscussion?: boolean;
  updateAsDraft: (id: number) => void;
  updateAsIssue: (id: number, issueToType: string | undefined) => void;
  isReply?: boolean;

  isLoadingUpdateAsDraft: boolean;
  isErrorUpdateAsDraft: boolean;
  isSuccessUpdateAsDraft: boolean;

  isLoadingUpdateAsIssue: boolean;
  isErrorUpdateAsIssue: boolean;
  isSuccessUpdateAsIssue: boolean;

  fileFormType: EProjectFileType;
  discussionFileType: EProjectDiscussionFileType;
  issueToTypes?: string[];
  useFetchFilesQueryKey?: QueryKey;
  canComment: boolean;
  canResponse: (discussion: IProjectDiscussion) => boolean;
  isFileUploading: boolean;
  onFileUploading: (uuid: string, isUploading: boolean) => void;
  isAnyActionLoading: boolean;
}

export const DraftDiscussionRow = ({
  discussion,
  onSelected,
  formFieldMethods,
  onDeleteDiscussion,
  isLoadingDeleteDiscussion = false,
  isErrorDeleteDiscussion = false,
  isSuccessDeleteDiscussion = false,
  updateAsDraft,
  updateAsIssue,
  isReply = false,

  isLoadingUpdateAsDraft = false,
  isErrorUpdateAsDraft = false,
  isSuccessUpdateAsDraft = false,

  isLoadingUpdateAsIssue = false,
  isErrorUpdateAsIssue = false,
  isSuccessUpdateAsIssue = false,

  fileFormType,
  discussionFileType,
  issueToTypes = [],
  useFetchFilesQueryKey,
  canComment,
  canResponse,
  isFileUploading,
  onFileUploading,
  isAnyActionLoading,
}: IDraftDiscussionRowProps) => {
  const [isLocalButtonClicked, setIsLocalButtonClicked] = React.useState(false);

  const myProfile = useMyUserProfile();

  const contentFormKey = React.useMemo(() => {
    return isReply ? DiscussionFormKeys.FURTHER_ACTION : DiscussionFormKeys.COMMENT;
  }, [isReply]);

  const isIssueFormKey = React.useMemo(() => {
    return isReply ? DiscussionFormKeys.IS_ISSUE_REPLY : DiscussionFormKeys.IS_ISSUE_COMMENT;
  }, [isReply]);

  const isSelected = formFieldMethods.watch(`${DiscussionFormKeys.FORM}[${discussion.id}].${isIssueFormKey}`);

  const author = React.useMemo(() => {
    const authorLocal = isReply ? discussion.repliedBy : discussion.commentedBy;
    return authorLocal ?? myProfile;
  }, [isReply, JSON.stringify(discussion)]);

  const localCanResponse = React.useMemo(() => {
    return canResponse(discussion);
  }, [canResponse, discussion]);

  const onClickDeleteDiscussion = (e: any) => {
    openPortal(e);
  };

  const onConfirmDeleteDiscussion = () => {
    if (onDeleteDiscussion && typeof onDeleteDiscussion === 'function') onDeleteDiscussion(discussion.id);
  };

  const { renderModal, openPortal } = useConfirmationModal({
    content: 'Confirm to Delete this discussion?',
    onConfirm: onConfirmDeleteDiscussion,
  });

  const validateContentRequired = (v?: string) => {
    return isSelected || isLocalButtonClicked ? (v?.length ?? 0) > 0 : true;
  };

  const shouldDisabled = React.useMemo(
    () => (!isReply && !canComment) || (isReply && !localCanResponse),
    [isReply, canComment, localCanResponse],
  );

  React.useEffect(() => {
    if (shouldDisabled) {
      onSelected(discussion.id, false);
      return;
    }
    onSelected(discussion.id, isSelected);
  }, [isSelected, shouldDisabled]);

  React.useEffect(() => {
    if (!isLoadingDeleteDiscussion && !isLoadingUpdateAsDraft && !isLoadingUpdateAsIssue)
      setIsLocalButtonClicked(false);
  }, [isLoadingDeleteDiscussion, isLoadingUpdateAsDraft, isLoadingUpdateAsIssue]);

  const generateIssueToButton = (issueToType: string | undefined) => {
    const issueToString = issueToType ? ` to ${issueToType}` : '';
    return (
      <LoadingButton
        key={issueToType || 'Issue'}
        type={ComponentType.Primary}
        prefixIconName="EnvelopeCheck"
        className="gcl-mr-sm"
        onClick={() => {
          setIsLocalButtonClicked(true);
          updateAsIssue(discussion.id, issueToType);
        }}
        isLoading={isLocalButtonClicked && isLoadingUpdateAsIssue}
        isError={isLocalButtonClicked && isErrorUpdateAsIssue}
        isSuccess={isLocalButtonClicked && isSuccessUpdateAsIssue}
        disabled={isFileUploading || isAnyActionLoading}
        timeToResetSuccessState={3000}
        timeToResetErrorState={5000}
      >
        {isReply ? 'Issue Reply' : `Issue Discussion${issueToString}`}
      </LoadingButton>
    );
  };

  const bottomComponent = React.useMemo(() => {
    return (
      <div
        className="gcl-gems-draft-discussion-row-bottom-btns"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {!shouldDisabled ? (
          <>
            <LoadingButton
              type={ComponentType.Secondary}
              className="gcl-mr-sm"
              prefixIconName="FolderCheck"
              onClick={() => {
                setIsLocalButtonClicked(true);
                updateAsDraft(discussion.id);
              }}
              isLoading={isLocalButtonClicked && isLoadingUpdateAsDraft}
              isError={isLocalButtonClicked && isErrorUpdateAsDraft}
              isSuccess={isLocalButtonClicked && isSuccessUpdateAsDraft}
              disabled={isFileUploading || isAnyActionLoading}
              timeToResetSuccessState={3000}
              timeToResetErrorState={5000}
            >
              Save Draft
            </LoadingButton>
            {isReply || !issueToTypes || issueToTypes.length <= 0
              ? generateIssueToButton(undefined)
              : issueToTypes.map((issueToType) => generateIssueToButton(issueToType))}
          </>
        ) : undefined}
        {!isReply && canComment && (
          <Button
            type={ComponentType.Danger}
            className="gcl-gems-draft-discussion-row-delete-btn"
            onClick={onClickDeleteDiscussion}
            disabled={isFileUploading || isAnyActionLoading}
            prefixIconName="XLg"
          />
        )}
      </div>
    );
  }, [
    isReply,
    canComment,
    shouldDisabled,
    isLocalButtonClicked,
    isLoadingUpdateAsDraft,
    isErrorUpdateAsDraft,
    isSuccessUpdateAsDraft,
    isLoadingUpdateAsIssue,
    isErrorUpdateAsIssue,
    isSuccessUpdateAsIssue,
    JSON.stringify(issueToTypes),
    isFileUploading,
    isAnyActionLoading,
  ]);

  return (
    <>
      {!isReply
        ? renderModal(
            isLocalButtonClicked && isLoadingDeleteDiscussion,
            isLocalButtonClicked && isErrorDeleteDiscussion,
            isLocalButtonClicked && isSuccessDeleteDiscussion,
          )
        : undefined}
      <DiscussionRowLayout
        useFetchFilesQueryKey={useFetchFilesQueryKey}
        isReply={isReply}
        disabled={shouldDisabled}
        discussionId={discussion.id}
        fileFormType={fileFormType}
        discussionFileType={discussionFileType}
        onFileUploading={onFileUploading}
        contentComponent={
          <>
            <div className="gcl-mb-xs">{author?.displayName || ''}</div>
            <Grid container flexWrap="nowrap" justifyContent="space-between">
              <Grid container item flex="0 1 auto">
                <TagItemView componentType={ComponentType.Warning} index={0} content="Not yet issued" />
              </Grid>
              <FormCheckbox
                className="gcl-gems-discussion-checkbox"
                formFieldMethods={formFieldMethods}
                checked={isSelected && !shouldDisabled}
                disabled={shouldDisabled}
                id={`${DiscussionFormKeys.FORM}${discussion.id}.${isIssueFormKey}`}
              />
            </Grid>
            <FormTextarea
              onClick={(event) => {
                event.stopPropagation();
              }}
              id={`${DiscussionFormKeys.FORM}.${discussion.id}.${contentFormKey}`}
              formFieldMethods={formFieldMethods}
              rules={{
                validate: {
                  requiredWhenSelected: (v: string) => validateContentRequired(v) || requiredRule.required.message,
                },
              }}
              disabled={shouldDisabled}
            />
          </>
        }
        authorName={author?.displayName ?? ''}
        authorUid={author?.uid}
        files={(isReply ? discussion?.responseAttachments : discussion?.commentAttachments) || []}
        isDraft
        isSelected={isSelected}
        onClickRow={() => {
          formFieldMethods.setValue(
            `${DiscussionFormKeys.FORM}[${discussion.id}].${isIssueFormKey}`,
            !isSelected && !shouldDisabled,
          );
        }}
        bottomComponent={bottomComponent}
      />
    </>
  );
};
